import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { ApiErrorWithResponse } from 'src/api/axiosClient/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { buildApikeyFormData, buildApikeyRequestData } from '../helpers';
import { useApikeyService } from '../services';
import { Apikey, ApikeyFormData, ApikeyPermission } from '../types';

export interface AddApiKeyModalController {
  isOpen: boolean;
  initFormData: ApikeyFormData | null;
  permissions: ApikeyPermission[];
  createdToken: string | null;
  open: (apikey?: Apikey) => void;
  close: () => void;
  submit: (formData: ApikeyFormData) => Promise<{ nameError?: string; validationError?: string } | void>;
  closeTokenModal: () => void;
}

export const useAddApiKeyModalController = (
  permissions: ApikeyPermission[] = [],
): AddApiKeyModalController => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [initFormData, setInitFormData] = useState<ApikeyFormData | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);

  const { createApikey, updateApikey } = useApikeyService();

  const open = useCallback((apikey?: Apikey) => {
    setInitFormData(buildApikeyFormData(apikey));
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const closeTokenModal = useCallback(() => {
    setCreatedToken(null);
  }, []);

  const submit = useCallback(
    async (formData: ApikeyFormData) => {
      if (!selectedCompanyId) {
        return;
      }

      const requestData = buildApikeyRequestData(formData);

      try {
        if (formData.id) {
          await updateApikey(requestData, formData.id, selectedCompanyId);
          close();
          return;
        }

        const created = await createApikey(requestData, selectedCompanyId);
        setCreatedToken(created.token);
        close();
      } catch (e: unknown) {
        const errorCode = (e as ApiErrorWithResponse)?.response?.data?.error ?? (e as ApiErrorWithResponse)?.error;
        if (errorCode === 'conflict') {
          return { nameError: 'Each API Key requires a unique name.' };
        }
        return { validationError: getApiErrorMessage(e, 'Something went wrong!') };
      }
    },
    [selectedCompanyId, createApikey, updateApikey, close],
  );

  return {
    isOpen: !!initFormData,
    initFormData,
    permissions,
    createdToken,
    open,
    close,
    submit,
    closeTokenModal,
  };
};
